import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Music, Video, Image as ImageIcon, Loader } from 'lucide-react';
import api from '../services/api';
import FileUpload from './FileUpload';

const typeIcon = (fileType) => {
    if (fileType === 'audio') return Music;
    if (fileType === 'video') return Video;
    return ImageIcon;
};

// Search-and-tag UX for the album's "add media" flow — modeled on
// ContributingArtistSelector.jsx, but searches the current user's own
// uploads (via GET /upload/search-mine) instead of other users' profiles.
const MyMediaSelector = ({
    selectedMedia = [],
    onMediaChange,
    placeholder = 'Search your own audio, video, and images...',
    disabled = false,
    className = ''
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [error, setError] = useState(null);
    // Which "upload new media" dropzone is currently open, if any — lets people
    // add brand-new audio/video/images straight into the album (with multi-file
    // support, since an album is rarely just one track) instead of only being
    // able to tag media that's already been uploaded and published elsewhere.
    const [uploadPanelType, setUploadPanelType] = useState(null); // 'audio' | 'video' | 'image' | null
    const [uploadErrors, setUploadErrors] = useState([]);

    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);
    // Mirrors selectedMedia synchronously so rapid, back-to-back onUpload
    // callbacks from a multi-file batch each append to the latest list
    // instead of racing against a stale `selectedMedia` prop closure.
    const selectedMediaRef = useRef(selectedMedia);
    useEffect(() => { selectedMediaRef.current = selectedMedia; }, [selectedMedia]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                searchMyUploads(searchTerm.trim());
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchMyUploads = async (query) => {
        try {
            setIsSearching(true);
            setError(null);

            const response = await api.get('/upload/search-mine', {
                params: { q: query, limit: 20 }
            });

            if (response.data.success) {
                const selectedIds = selectedMedia.map(m => m.id);
                const filtered = response.data.data.filter(item => !selectedIds.includes(item.id));
                setSearchResults(filtered);
                setShowDropdown(filtered.length > 0);
            }
        } catch (err) {
            console.error('Error searching your uploads:', err);
            setError('Failed to search your uploads');
            setSearchResults([]);
            setShowDropdown(false);
        } finally {
            setIsSearching(false);
        }
    };

    const selectItem = (item) => {
        onMediaChange([...selectedMedia, item]);
        setSearchTerm('');
        setSearchResults([]);
        setShowDropdown(false);
        setError(null);
    };

    const removeItem = (itemId) => {
        onMediaChange(selectedMedia.filter(item => item.id !== itemId));
    };

    // Fires once per file for a multi-file batch (FileUpload uploads them
    // sequentially and calls onUpload after each one completes) — the raw
    // upload endpoints already auto-title from the filename and return
    // everything needed to tag it in immediately, no separate metadata step.
    const handleNewUpload = (uploadData, fileType) => {
        const newItem = {
            id: uploadData.id,
            file_type: fileType,
            title: uploadData.title,
            original_filename: uploadData.originalName,
            cover_image_url: fileType === 'image' ? (uploadData.optimizedUrl || uploadData.url || null) : null
        };
        const updated = [...selectedMediaRef.current, newItem];
        selectedMediaRef.current = updated;
        onMediaChange(updated);
    };

    const handleUploadError = (message) => {
        setUploadErrors(prev => [...prev, message]);
    };

    const handleInputFocus = () => {
        if (searchResults.length > 0) setShowDropdown(true);
    };

    return (
        <div className={`relative ${className}`}>
            <label className="block text-sm font-medium text-gray-300 mb-2">
                Album Media
                {selectedMedia.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400">({selectedMedia.length})</span>
                )}
            </label>

            {selectedMedia.length > 0 && (
                <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                        {selectedMedia.map((item) => {
                            const Icon = typeIcon(item.file_type);
                            return (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-2 bg-purple-600/20 border border-purple-600/40 rounded-full px-3 py-1 text-sm"
                                >
                                    {item.cover_image_url ? (
                                        <img
                                            src={item.cover_image_url}
                                            alt={item.title}
                                            width="20"
                                            height="20"
                                            className="w-5 h-5 rounded-full object-cover"
                                        />
                                    ) : (
                                        <Icon className="w-3.5 h-3.5 text-purple-300" />
                                    )}
                                    <span className="text-purple-200 font-medium truncate max-w-[10rem]">
                                        {item.title || item.original_filename}
                                    </span>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => removeItem(item.id)}
                                            className="text-purple-200 hover:text-red-400 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="relative" ref={dropdownRef}>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={handleInputFocus}
                        placeholder={placeholder}
                        disabled={disabled}
                        className="w-full bg-gray-800/50 border border-gray-600 rounded-lg pl-10 pr-10 py-3 text-white placeholder-gray-400 focus:border-purple-600 focus:ring-1 focus:ring-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {isSearching && (
                        <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-400 w-4 h-4 animate-spin" />
                    )}
                </div>

                {showDropdown && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                        {searchResults.map((item) => {
                            const Icon = typeIcon(item.file_type);
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => selectItem(item)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-700 transition-colors border-b border-gray-700 last:border-b-0"
                                >
                                    {item.cover_image_url ? (
                                        <img
                                            src={item.cover_image_url}
                                            alt={item.title}
                                            width="40"
                                            height="40"
                                            className="w-10 h-10 rounded-lg object-cover"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-gray-400" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-white truncate">
                                            {item.title || item.original_filename}
                                        </div>
                                        <div className="text-xs text-gray-400 capitalize">{item.file_type}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                {showDropdown && searchResults.length === 0 && searchTerm.length >= 2 && !isSearching && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 px-4 py-3">
                        <div className="text-center text-gray-400">No matching uploads found for "{searchTerm}"</div>
                    </div>
                )}
            </div>

            {error && <div className="mt-2 text-red-400 text-sm">{error}</div>}

            {!error && selectedMedia.length === 0 && (
                <div className="mt-2 text-gray-500 text-xs">
                    Search for audio, video, or images you've already uploaded, or upload something new below.
                </div>
            )}

            {/* Upload new media directly into the album — multiple files at
                once, since an album is rarely just one track. Titles are
                auto-derived from each filename; full metadata (description,
                access level, etc.) can still be edited per-item afterward
                from that upload's own page. */}
            <div className="mt-3 flex flex-wrap gap-2">
                {['audio', 'video', 'image'].map((type) => {
                    const Icon = typeIcon(type);
                    const label = type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'Audio';
                    const active = uploadPanelType === type;
                    return (
                        <button
                            key={type}
                            type="button"
                            onClick={() => setUploadPanelType(active ? null : type)}
                            disabled={disabled}
                            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                active
                                    ? 'bg-purple-600/20 border-purple-600/50 text-purple-300'
                                    : 'bg-gray-800/50 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                        >
                            {active ? <X className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                            Upload New {label}
                        </button>
                    );
                })}
            </div>
            <p className="mt-2 text-gray-500 text-xs">
                Once the media is uploaded you will be able to edit the details of the media.
            </p>

            {uploadPanelType && (
                <div className="mt-3">
                    <FileUpload
                        key={uploadPanelType}
                        type={uploadPanelType}
                        multiple
                        onUpload={(data) => handleNewUpload(data, uploadPanelType)}
                        onError={handleUploadError}
                        className="max-w-md"
                    >
                        Drop {uploadPanelType === 'image' ? 'images' : `${uploadPanelType} files`} here or{' '}
                        <span className="text-blue-400">browse</span> — select multiple to add them all at once
                    </FileUpload>
                </div>
            )}

            {uploadErrors.length > 0 && (
                <div className="mt-2 space-y-1">
                    {uploadErrors.map((msg, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-red-400 text-xs">
                            <span>{msg}</span>
                            <button
                                type="button"
                                onClick={() => setUploadErrors(prev => prev.filter((_, idx) => idx !== i))}
                                className="text-red-400 hover:text-red-300"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyMediaSelector;
